<template>
    <conditional-swiper
        :swiperize="isKiosk"
        class="cz2__steps-options__checkout"
        :class="[
            {
                'cz2__steps-options__checkout--kiosk': isKiosk,
            },
        ]"
        @scrolled="updateRenderer"
    >
        <div
            class="cz2__steps-options__checkout-gradient"
            :class="`cz2__steps--sb-type-${spellingBeeType}`"
        >
            <funko-logos class="cz2__checkout-logo" />

            <div class="cz2__steps-options__checkout-wrapper">
                <div
                    class="cz2__steps-options__checkout-wrapper__header"
                    :class="[
                        isSpellingBee ? 'cz2__font-sb' : 'cz2__font-inter-bold',
                    ]"
                    v-if="isKiosk"
                >
                    {{ $t('REVIEW_AND_PRINT_TICKET') }}
                </div>

                <div
                    ref="rendererPlaceholder"
                    class="cz2__renderer-placeholder"
                />

                <img
                    v-if="isKiosk"
                    src="../assets/images/pop-yourself-logo.svg"
                    alt="Pop! Yourself Logo"
                    class="cz2__splash__logo"
                >

                <div class="cz2__checkout__items">
                    <h1 class="cz2__checkout-items__pop">
                        {{ `${$t('POP_YOURSELF')} - ${packType}` }}
                    </h1>

                    <h2 class="cz2__checkout-items__box">
                        {{ $t('REVIEW') }}
                    </h2>

                    <div>
                        <div class="cz2__checkout-items__header" />

                        <transition-group
                            name="cz2__fade-group-plain"
                            class="cz2__position-relative"
                        >
                            <div
                                v-for="item in cartSorted"
                                :key="item.id || item.type"
                                class="cz2__checkout-items__item"
                                :class="[`cz2__checkout-items__item--${item.type}`]"
                            >
                                <template v-if="item.type === 'preorder'">
                                    <div class="cz2__preorder-message">
                                        {{ $t('PREORDER_DATE', { date: item.preorder }) }}
                                    </div>
                                </template>

                                <template v-else>
                                    <div class="cz2__checkout-item__wrapper">
                                        <div
                                            :class="{
                                                'cz2__checkout-item__icon--pop': item.type === 'main',
                                                'cz2__checkout-item__icon--sticker': item.type === 'sticker',
                                            }"
                                            class="cz2__checkout-item__icon"
                                        >
                                            <div
                                                v-if="item.canEdit"
                                                class="cz2__checkout-item__edit"
                                            >
                                                <button
                                                    @click.prevent="editItem(item)"
                                                    :aria-label="$t('ITEM_EDIT')"
                                                    :title="$t('ITEM_EDIT')"
                                                >
                                                    <span>
                                                        {{ $t('ITEM_EDIT') }}
                                                    </span>
                                                </button>
                                            </div>

                                            <div
                                                v-else-if="item.canRemove"
                                                class="cz2__checkout-item__delete"
                                            >
                                                <button
                                                    @click.prevent="removeItem()"
                                                    :aria-label="$t('ITEM_REMOVE')"
                                                    :title="$t('ITEM_REMOVE')"
                                                >
                                                    <span>
                                                        {{ $t('ITEM_REMOVE') }}
                                                    </span>
                                                </button>
                                            </div>

                                            <div
                                                v-else
                                                class="cz2__checkout-item__spacer"
                                            />

                                            <template v-if="item.icon">
                                                <span
                                                    class="cz2__checkout-item__icon__wrapper"
                                                    :class="{ 'cz2__buddy-pack-main-icon': item.type === 'buddyPack' }"
                                                >
                                                    <span
                                                        v-for="(i, index) in item.icon.slice(0, 3)"
                                                        :key="index"
                                                        :style="{ backgroundImage: `url(&quot;${checkImage(i)}&quot;)` }"
                                                        class="cz2__checkout-item__icon--image"
                                                    >
                                                        <span
                                                            v-if="item.counts && item.counts[index] && item.counts[index] > 1"
                                                            class="cz2__checkout-item__icon-count"
                                                        >
                                                            <span>{{item.counts[index]}}</span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </template>
                                        </div>

                                        <div>
                                            <div class="cz2__checkout-item__name-table">
                                                {{ getItemName(item) }}
                                            </div>

                                            <div
                                                v-if="item.packBuddyNames"
                                                class="cz2__checkout-item__name-table cz2__checkout-item__buddy-names"
                                            >
                                                {{ item.packBuddyNames.join(', ') }}
                                            </div>

                                            <span v-if="item.packImages" class="cz2__checkout-item__icon">
                                                <div class="cz2__checkout-item__icon__wrapper">
                                                    <div
                                                        v-for="image in item.packImages"
                                                        :key="image"
                                                        :style="{ backgroundImage: `url(${image})` }"
                                                        class="cz2__checkout-item__icon--image"
                                                    />
                                                </div>
                                            </span>
                                        </div>
                                    </div>

                                    <div class="cz2__checkout-item__filler" />
                                    <div class="cz2__checkout-item__price">
                                        {{ getFormattedPrice(item) }}
                                    </div>
                                </template>
                            </div>

                            <special-packaging
                                v-if="seasonalBox && !seasonalBox.selected"
                                :key="'special'"
                            />

                            <box-style-options
                                v-if="specialBoxes.length > 0"
                                :key="'boxStyles'"
                                class="cz2__checkout-items__boxes"
                            />

                            <premium-packaging
                                v-if="isKiosk"
                                :key="'protectors'"
                                class="cz2__checkout-items__protectors"
                            />

                            <div
                                :key="'totals'"
                                class="cz2__checkout-items__totals"
                                v-if="isKiosk"
                            >
                                <div class="cz2__checkout-items__totals__item">
                                    {{ $t('TOTALS_TEXT') }}
                                </div>

                                <div class="cz2__checkout-items__totals__price">
                                    {{ totalPrice }}
                                </div>
                            </div>
                        </transition-group>

                        <premium-packaging
                            v-if="!isKiosk"
                            :key="'protectors'"
                            class="cz2__checkout-items__protectors"
                        />

                        <div
                            key="totals"
                            role="row"
                            class="cz2__checkout-items__totals"
                            v-if="!isKiosk"
                        >
                            <div
                                role="cell"
                                class="cz2__checkout-items__totals__item"
                            >
                                {{ $t('TOTALS_TEXT') }}
                            </div>

                            <div
                                role="cell"
                                class="cz2__checkout-items__totals__price"
                            >
                                {{ totalPrice }}
                            </div>
                        </div>

                        <price-disclaimer />

                        <div role="table" class="cz2__checkout-items__your-info" v-if="isKiosk">
                            <div
                                role="row"
                                class="cz2__checkout-items__header"
                            >
                                <div class="cz2__checkout-item__icon cz2__checkout-item__icon--pop" />

                                <div
                                    role="columnheader"
                                    class="cz2__checkout-items__header__item"
                                >
                                    {{ $t('YOUR_INFO') }}
                                </div>
                            </div>

                            <transition-group
                                name="cz2__fade-group-plain"
                                class="cz2__position-relative"
                            >
                                <div
                                    role="row"
                                    :key="'your-info'"
                                >
                                    <template>
                                        <div
                                            role="cell"
                                            class="cz2__checkout-items__item"
                                        >
                                            <div class="cz2__checkout-item__name">
                                                <span>
                                                    {{ $t('YOUR_NAME') }}
                                                </span>
                                                <span>
                                                    {{ $store.state.customizer.contactName }}
                                                </span>
                                            </div>
                                            <div
                                                role="cell"
                                                class="cz2__checkout-item__edit"
                                            >
                                                <button
                                                    @click.prevent="editContact"
                                                    :aria-label="$t('ITEM_EDIT')"
                                                    :title="$t('ITEM_EDIT')"
                                                >
                                                    <span>
                                                        {{ $t('ITEM_EDIT') }}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            role="cell"
                                            class="cz2__checkout-items__item"
                                        >
                                            <div class="cz2__checkout-item__name">
                                                <span>
                                                    {{ $t('YOUR_PHONE_NUMBER') }}
                                                </span>
                                                <span>
                                                    {{ contactPhone }}
                                                </span>
                                            </div>
                                            <div
                                                role="cell"
                                                class="cz2__checkout-item__edit"
                                            >
                                                <button
                                                    @click.prevent="editContact"
                                                    :aria-label="$t('ITEM_EDIT')"
                                                    :title="$t('ITEM_EDIT')"
                                                >
                                                    <span>
                                                        {{ $t('ITEM_EDIT') }}
                                                    </span>
                                                </button>
                                                <div class="cz2__checkout-item__filler" />
                                            </div>
                                        </div>
                                    </template>
                                </div>

                                <div
                                    :key="'totals'"
                                    role="row"
                                    class="cz2__checkout-items__totals"
                                    v-if="!isKiosk"
                                >
                                    <div
                                        role="cell"
                                        class="cz2__checkout-items__totals__item"
                                    >
                                        {{ $t('TOTALS_TEXT') }}
                                    </div>

                                    <div
                                        role="cell"
                                        class="cz2__checkout-items__totals__price"
                                    >
                                        {{ totalPrice }}
                                    </div>
                                </div>
                            </transition-group>

                        </div>
                        <steps-options-checkout-add-to-cart class="hide--below-md" />
                    </div>
                </div>

                <div class="cz2__checkout__copy" v-if="isKiosk">
                    <span>
                        {{ $t('KIOSK_CHECKOUT_COPY') }}
                    </span>
                    <span class="cz2__checkout__copy--bold">
                        {{ $t('ALL_SALES_FINAL') }}
                    </span>
                </div>
            </div>
        </div>

        <section
            v-if="showRecommendations || showStickers"
            ref="carousel"
            class="cz2__checkout__carousel"
        >
            <div
                class="cz2__checkout__carousel__heading"
                :class="headerFontClass"
            >
                <h2 class="cz2__checkout__carousel__heading-content">
                    <span v-if="isSpellingBee">{{ $t('EM_DASH') }}&nbsp;</span>
                    {{ $t('CHECKOUT_ADD') }}
                    <span v-if="isSpellingBee">&nbsp;{{ $t('EM_DASH') }}</span>
                </h2>
            </div>

            <span class="cz2__sr-only">
                {{ $t('SR_ACTIVATING') }}
            </span>

            <products-carousel
                v-if="showRecommendations"
            />

            <stickers-carousel
                v-else-if="showStickers"
                :stickers="stickers"
            />
        </section>

        <div class="cz2__checkout__atc--sticky hide--above-md">
            <steps-options-checkout-add-to-cart />
        </div>
    </conditional-swiper>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__steps-options__checkout {
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;

            color: black;

            .cz2__checkout-logo {
                position: absolute;
                top: 16px;
                left: var(--layout-spacing);
            }

            .cz2__steps-options__checkout-gradient {
                flex: 1 1 auto;
                // @include bg(gradientBlue);
            }

            .cz2__steps-options__checkout-wrapper {
                padding-bottom: 40px;

                width: 100%;

                display: flex;
                flex-direction: column;

                .cz2__renderer-placeholder {
                    height: 300px;
                }
            }

            &.cz2__steps-options__shared {
                bottom: auto;
                top: 0px;
                height: 100%;

                pointer-events: none;
            }

            &.cz2__steps-options__checkout--kiosk {
                height: var(--vh100, 100vh);
                z-index: 0;
            }

            .cz2__preorder-message {
                font-family: var(--cz-font-400-family);
                font-weight: var(--cz-font-400-weight);
                font-style: normal;
                font-size: 15px;
                line-height: 22px;
                letter-spacing: 1px;
                text-transform: uppercase;
                color: white;
            }

            .cz2__checkout-items__item--buddyPack {
                border-top: 2px solid rgba(237, 237, 237, 1);
                border-bottom: 2px solid rgba(237, 237, 237, 1);
                padding: 10px 0;
                height: fit-content !important;
                align-items: flex-start !important;

                .cz2__checkout-item__wrapper {
                    align-items: flex-start;
                }

                .cz2__checkout-item__icon {
                    .cz2__buddy-pack-main-icon {
                        margin: 0;

                        .cz2__checkout-item__icon--image {
                            border-radius: 0;
                            background-color: transparent;
                            background-size: cover;
                        }
                    }
                }

                .cz2__checkout-item__buddy-names {
                    font-weight: 400;
                    font-size: 14px;
                }

                .cz2__checkout-item__icon__wrapper {
                    margin: 0 8px;
                    gap: 8px;
                }
            }

            .cz2__checkout-items__totals {
                display: flex;
                justify-content: flex-start;
                align-items: center;
                text-transform: uppercase;

                height: 55px;
            }

            .cz2__checkout-items__totals,
            .cz2__checkout-items__boxes {
                border-top: 2px solid rgba(237, 237, 237, 1);
            }

            .cz2__checkout-items__protectors,
            .cz2__checkout-items__boxes {
                padding-bottom: 24px;
            }

            .cz2__checkout-items__boxes {
                padding-top: 24px;
            }

            .cz2__checkout-items__totals__item {
                flex: 1 0 0;

                @include font(formRegular);
            }

            .cz2__disclaimer {
                margin-top: -8px;
                margin-bottom: 8px;
            }

            .cz2__checkout-items__totals__price {
                flex: 0 0 auto;

                @include font(h4);
                font-size: 20px;
                letter-spacing: 1px;
            }

            .cz2__checkout-item__wrapper {
                display: flex;
                align-items: center;
            }

            .cz2__checkout-items__item {
                margin-bottom: 8px;

                display: flex;
                justify-content: space-between;
                align-items: center;

                height: 40px;
                width: 100%;

                &--preorder {
                    margin-top: -20px;
                    border-top: none !important;
                }
            }

            :nth-child(2 of .cz2__checkout-items__item--main) {
                margin-top: 8px;
                border-top: 2px solid rgba(237, 237, 237, 1);
                padding-top: 8px;
                height: 52px;
            }

            .cz2__checkout-item__icon {
                position: relative;

                display: flex;

                .cz2__checkout-item__icon__wrapper {
                    display: flex;
                    flex-direction: row;

                    .cz2__checkout-item__icon--image {
                        position: relative;

                        width: 40px;
                        height: 40px;

                        border-radius: 9999px;
                        background-color: #EDEDED;
                        background-size: contain;
                        background-position: center;
                        background-repeat: no-repeat;
                    }
                }

                &.cz2__checkout-item__icon--sticker span {
                    background-size: 25px 32px;
                }

                .cz2__checkout-item__icon-count {
                    position: absolute;

                    right: 0px;
                    top: 0px;

                    width: 22px;
                    height: 22px;

                    border-radius: 22px;

                    background: rgba(39, 39, 39, 0.5);

                    text-align: center;

                    span {
                        width: auto;
                        height: auto;
                        margin-left: 2px;

                        @include font(h3);
                        line-height: 22px;
                    }
                }
            }

            .cz2__checkout-item__name,
            .cz2__checkout-item__name-table {
                margin-bottom: 8px;
                text-align: left;
                @include font(h3);
                font-size: 15px;

                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                // Max-height fallback for browsers that don't support line-clamp.
                max-height: 66px;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .cz2__checkout-item__name-table {
                margin: 0 8px;
                text-transform: none;
                font-weight: 600;
            }

            .cz2__checkout-item__spacer {
                width: 46px;
                height: 46px;
            }

            .cz2__checkout-item__edit,
            .cz2__checkout-item__delete {
                flex: 0 0 auto;

                button {
                    position: relative;

                    width: 46px;
                    height: 46px;

                    border: none;
                    background: none;

                    span {
                        display: none;
                    }

                    cursor: pointer;

                    &:after {
                        content: '';

                        position: absolute;

                        left: 50%;
                        top: 50%;

                        transform: translate(-50%, -50%);

                        width: 24px;
                        height: 24px;
                    }
                }
            }

            .cz2__checkout-item__edit {
                button {
                    &:after {
                        background: transparent url(../assets/images/branded/pokemon/pokemon-edit.svg) no-repeat center/contain;
                    }
                }
            }

            .cz2__checkout-item__delete {
                button {
                    &:after {
                        background: transparent url(../assets/images/branded/pokemon/pokemon-delete.svg) no-repeat center/contain;
                    }
                }
            }

            .cz2__checkout-item__price {
                text-align: right;
                white-space: nowrap;

                @include font(bodyRegular);
            }

            .cz2__checkout__items {
                z-index: 1;
                width: 100%;

                padding: 0 var(--layout-spacing);

                pointer-events: auto;

                @include respond-above(sm) {
                    padding: 32px 32px 52px 32px;
                }

                @include respond-above(xl) {
                    .cz2__checkout-item__price {
                        max-width: none;
                        white-space: nowrap;
                    }

                    width: 50%;
                }
            }

            .cz2__checkout-items__pop {
                margin-bottom: 10px;
                @include font(formBold);
                text-transform: uppercase;
            }

            .cz2__checkout-items__box {
                margin-bottom: 8px;

                @include font(h1);

                @include respond-above(xl) {
                    @include font(feature);

                    &.small {
                        font-size: 26px;
                    }
                }
            }

            .cz2__checkout-items__header {
                margin-bottom: 8px;

                display: flex;

                width: 100%;

                padding-bottom: 17px;

                border-bottom: 2px solid #fff;

                @include font(formRegular);
            }

            .cz2__checkout-items__header__item {
                flex: 1 0 0;

                text-align: left;
                font-size: 20px;
            }

            .cz2__checkout-items__header__price {
                flex: 1 0 0;

                text-align: right;
            }

            .cz2__checkout__carousel {
                padding: 0 var(--layout-spacing);

                width: 100%;

                pointer-events: auto;

                .cz2__checkout__carousel__heading {
                    position: relative;
                    margin: -20px 0 20px 4px;

                    padding: 0 32px 8px 8px;

                    height: var(--mainNavHeight);
                    max-width: fit-content;

                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 34px;

                    .cz2__checkout__carousel__heading-content {
                        position: relative;

                        display: flex;

                        text-transform: uppercase;
                        font-style: italic;
                        color: color(primaryWhite);
                    }

                    &:before {
                        position: absolute;
                        left: -8px;

                        width: 110%;
                        height: 100%;
                        content: '';
                        clip-path: polygon(0 0, 98% 8%, 94% 90%, 2% 100%);
                        background-color: color(v2Red);
                    }
                }
            }

            .cz2__checkout__atc--sticky {
                position: sticky;
                z-index: 2;
                bottom: 0;
                box-shadow: 0px -12px 11px -18px rgba(0, 0, 0, 0.55);
                width: 100%;

                padding: var(--layout-spacing);
                background-color: color(v2BlueMedium);

                .cz2__btn {
                    height: 39px;
                }
            }

            @include respond-above(md) {
                .cz2__checkout-logo {
                    top: 24px;
                    left: 32px;
                }

                .cz2__checkout__carousel {
                    padding-bottom: 32px;
                }

                .cz2__disclaimer {
                    margin-top: -16px;
                    margin-bottom: 32px;
                }
            }

            @include respond-above(xl) {
                .cz2__steps-options__checkout-wrapper {
                    margin: 0 auto;
                    padding-bottom: 0;
                    max-width: 1280px;

                    flex-direction: row;

                    .cz2__renderer-placeholder {
                        margin-top: 96px;
                        height: auto;
                        max-height: 575px;
                        flex: 1 1 0%;
                    }
                }

                .cz2__checkout__carousel__heading {
                    margin-top: -28px;
                    margin-left: 120px;
                    margin-bottom: 36px;
                }

                .cz2__checkout__carousel__heading-content {
                    font-size: 45px;
                }
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        // .cz2__customizer {
        //     overflow-y: auto;
        // }

        .cz2__steps-options__checkout {
            .cz2__checkout-item__icon {
                max-width: 100px !important;
            }

            .cz2__checkout__items {
                width: 100%;
            }

            .cz2__checkout-items__pop {
                display: none;
            }

            .cz2__checkout-items__box {
                display: none;
            }

            .cz2__checkout-items__header {
                .cz2__checkout-item__icon--pop {
                    display: none;
                }
            }

            .cz2__steps-options__checkout-gradient {
                display: flex;
            }

            .cz2__steps-options__checkout-wrapper {
                display: flex;
                flex-direction: column;
            }

            .cz2__steps-options__checkout-wrapper__header {
                width: 100%;
                text-align: center;
                margin-top: 210px;
                font-size: 44px;
            }

            .cz2__renderer-placeholder {
                width: var(--vw100, 100vw);
                max-height: 380px;
                margin-top: 0;
            }

            .cz2__checkout__items {
                top: 500px;
            }

            .cz2__splash__logo {
                width: 222px;
                height: 195px;
                position: absolute;
                top: 16px;
                right: 16px;

                z-index: 1000;
            }

            .cz2__checkout-items__item {
                display: flex;
                .cz2__checkout-item__name {
                    max-width: 1000px;
                    width: auto;
                }
                .cz2__checkout-item__filler {
                    flex: 1;
                }
            }

            .cz2__checkout-items__totals {
                border-top: 4px solid rgba(255, 255, 255);
                padding-top: 16px;
            }

            .cz2__checkout-items__totals__item {
                font-size: 20px;
            }

            .cz2__checkout-item__protectors {
                border-top: none;
            }

            .cz2__checkout__copy {
                text-transform: initial;
                font-size: 24px;
                font-family: var(--cz-font-400-family);
                padding: 40px 53px 0 53px;
            }

            .cz2__checkout__copy--bold {
                text-transform: uppercase;
                font-family: var(--cz-font-700-family);
                font-weight: var(--cz-font-700-weight);
            }

            .cz2__checkout__items {
                padding: 0 53px;
            }

            .cz2__checkout__carousel__heading {
                margin-bottom: 0;
            }

            .cz2__checkout__carousel__heading-content {
                font-size: 81px;
                font-family: var(--cz-font-700-family);
                font-weight: var(--cz-font-700-weight);
                margin-left: 24px;;
            }

            .cz2__checkout-items__header {
                font-size: 20px;
            }

            .cz2__checkout-item__name {
                max-width: initial;
                width: auto;
                flex: 0 0 auto;
            }

            .cz2__checkout-items__your-info {
                padding-top: 48px;
                margin-bottom: 40px;
                border-bottom: 2px solid #FFFFFF;
                .cz2__checkout-item__name {
                    text-transform: none;
                }
                .cz2__checkout-item__edit {
                    flex: 1;
                    padding-left: 16px;
                }
            }

            .uppercase {
                text-transform: uppercase;
            }

            .cz2__renderer-placeholder {
                height: 380px;
                min-height: 380px;
            }

            .cz2__steps-options__checkout-wrapper {
                flex: 1;
                padding-bottom: 64px;
            }
        }
    }

    #cz2.cz2.bg-darker-blue-gradient {
        // background-image: url("../assets/images/branded/sb-final/SB-Background-Pattern-1_D@2x.jpg") !important;
        // background-size: cover;
        // background-position: bottom;
    }

    #cz2.cz2.cz2__is-sb.cz2__is-kiosk {
        .cz2__steps {
            overflow: hidden;
        }

        .cz2__steps-options__checkout--kiosk {
             height: var(--vh100, 100vh);

            .cz2__steps-options__checkout-wrapper__header {
                margin-top: 64px;
            }
        }

        .cz2__steps-options__checkout-gradient {
            height: auto;
        }
    }

    #cz2.cz2.cz2__is-sb {
        .cz2__checkout-items__totals__item {
            font-weight: var(--cz-font-700-weight);
            letter-spacing: 1.5px;
        }
        .cz2__checkout-items__box {
            font-family: 'BluuNext-bold', sans-serif;
        }
        .cz2__checkout__items {
            padding-bottom: 32px;
        }
        .cz2__checkout-item__delete, .cz2__checkout-item__edit {
            button {
                &:after {
                    filter: invert(1) brightness(0);
                }
            }
        }
        .cz2__checkout-item__protectors {
            border-top: #000 2px solid;
        }
        .cz2__checkout-items__item, .cz2__checkout-items__totals {
            border-top: #000 2px solid;
        }
        .cz2__steps-options {
            .cz2__checkout__carousel {
                background-image: url("../assets/images/branded/sb-final/SB-Background-Pattern-1_D@2x.jpg");
                background-size: cover;
                background-position: center;
            }
            .cz2__checkout-items__item{
                border-top: #000 1px solid;
            }
            .cz2__checkout__carousel__heading {
                width: 100%;
                max-width: 100vw;
                padding: 16px 0 16px 0;
                margin: 0;
                &:before {
                    background-color: transparent;
                }

                .cz2__checkout__carousel__heading-content {
                    color: color(sbDarkBlue);
                    font-weight: var(--cz-font-700-weight);
                    font-family: 'Proxima Nova', sans-serif;
                    font-style: normal;
                    font-size: 20px;
                    margin: auto;
                    text-align: center;

                    @include respond-above(sm) {
                        font-size: 32px;
                    }
                }
            }
        }

        .cz2__steps-options__checkout-gradient {
            min-height: fit-content;

            @include respond-below(sm) {
                height: auto;
            }
        }

        .cz2__steps-options__checkout-wrapper {
            color: #000;
            padding-bottom: 0;
        }

        .cz2__checkout-items__header {
            border-bottom: 2px solid #000;
        }

        .cz2__checkout__atc--sticky {
            background-color: color(sbDarkGold);
            box-shadow: none;
            pointer-events: auto;
        }
    }

    #cz2.cz2.cz2__is-mariners {
        .cz2__steps-options__checkout {
            .cz2__steps .cz2__funko-logo-wrapper {
                @include respond-above(xl) {
                    margin-right: 65%;
                }
                @include respond-above(xl) {
                    margin-right: 75%;
                }
            }
        }

        @include respond-above(xl) {
            .cz2__steps-options__checkout {
                // padding-top: 100px;
            }
        }

        .cz2__checkout__atc--sticky {
            border-top: 1px solid color(pokemonGray);
            background-color: white;
        }
    }
</style>

<script>
    import axios from 'axios';

    import { AsYouType } from 'libphonenumber-js';

    import { formatPrice } from '../helpers/formatPrice';

    import * as constants from '../constants';

    import SpecialPackaging from './SpecialPackaging.vue';
    import BoxStyleOptions from './BoxStyleOptions.vue';
    import PremiumPackaging from './PremiumPackaging.vue';
    import StepsOptionsCheckoutAddToCart from './StepsOptionsCheckoutAddToCart.vue';
    import StickersCarousel from './StickersCarousel.vue';
    import ProductsCarousel from './ProductsCarousel.vue';
    import Btn from './ui/Btn.vue';
    import ConditionalSwiper from './ui/ConditionalSwiper.vue';
    import PriceDisclaimer from './PriceDisclaimer.vue';
    import FunkoLogos from './FunkoLogos.vue';

    export default {
        components: {
            SpecialPackaging,
            BoxStyleOptions,
            PremiumPackaging,
            StepsOptionsCheckoutAddToCart,
            StickersCarousel,
            ProductsCarousel,
            Btn,
            ConditionalSwiper,
            PriceDisclaimer,
            FunkoLogos,
        },

        data() {
            return {
                view: 'pop',
                mainIcon: null,
            };
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            options() {
                return this.$store.state.customizer.options;
            },

            cart() {
                // Don't display protector in cart table
                // eslint-disable-next-line max-len
                const list = this.$store.getters.cart.filter((item) => item.type !== 'protector' && item.type !== 'specialbox' && item.type !== 'discreetPackaging');

                return list;
            },

            cartSorted() {
                const cartItems = this.cart;
                const sortOrder = ['main', 'extra', 'pet', 'buddyPack', 'sticker', 'protector', 'discreetPackaging'];

                cartItems.sort((a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type));

                return cartItems;
            },

            totalPrice() {
                return formatPrice(this.$store.state, this.$store.getters.cartTotal, 'standard');
            },

            isTwoPack() {
                return this.$store.getters.isTwoPack;
            },

            boxNames() {
                return Object.values(this.$store.state.customizer.boxNamesByController);
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            contactPhone() {
                return new AsYouType('US').input(this.$store.state.customizer.contactPhone || '');
            },

            seasonalBox() {
                console.log('cart sorted', this.cartSorted, this.cart);
                return this.$store.getters.seasonalBox;
            },

            isSpellingBee() {
                return this.$store.state.isSpellingBee;
            },

            headerFontClass() {
                return this.$store.getters.headerFontClass;
            },

            spellingBeeType() {
                return this.$store.state.spellingBeeType ?? 'none';
            },

            stickers() {
                if (this.viewController == null) {
                    return [];
                }

                const placement = this.viewController.findPlacement(constants.PLACEMENT_STICKERS);
                const available = placement.components.filter((c) => !c.placeholder && this.viewController.isAvailable(c));
                const stickers = available.map((c, index) => ({
                    index,
                    code: c.code,
                    component: c,
                    name: c.custom['fun-display-name'],
                    image: `https://api.images.drivecommerce.com/api/v1/image/funko/CZ-${c.placementIndependentCode}?fmt=jpeg`,
                }));

                stickers.forEach((s, index) => {
                    s.index = index;
                });

                return stickers;
            },

            globalSettings() {
                return this.$store.state.customizer.globalSettings;
            },

            showRecommendations() {
                if (this.isKiosk || this.isMariners) {
                    return false;
                }

                if (!this.$store.state.customizer.options.einsteinRecommenderEnabled) {
                    return false;
                }

                // eslint-disable-next-line no-constant-condition
                if (true || this.globalSettings?.blueprint?.custom['fun-show-recommendations'] === 'true') {
                    const products = this.$store.state.customizer.recommendations;

                    if (products != null) {
                        return true;
                    }
                }

                return false;
            },

            showStickers() {
                if (this.showRecommendations || this.isMariners) {
                    return false;
                }

                if (this.globalSettings?.blueprint?.custom['fun-show-stickers'] === 'false') {
                    return false;
                }

                return true;
            },

            isMariners() {
                return this.$store.state.isMariners;
            },

            packType() {
                return this.isTwoPack ? this.$t('CHECKOUT_PACK_TWO') : this.$t('CHECKOUT_PACK_SINGLE');
            },

            specialBoxes() {
                return this.$store.getters.specialBoxes;
            },
        },

        watch: {
            cart() {
                this.$store.dispatch('saveCart', this.cart);
            },
        },

        mounted() {
            this.updateNavigation();

            this.$store.dispatch('getRecommendations');

            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'pop_people_buy_pop',
                });
            }

            this.emitter.$emit('setRendererPlaceholder', {
                owner: 'checkout',
                placeholder: this.$refs.rendererPlaceholder,
            });

            this.$store.dispatch('bumpVersion');

            this.saveRecipe();
        },

        beforeDestroy() {
            // Stop watching.
            this.emitter.$emit('setRendererPlaceholder', {
                owner: 'checkout',
            });
        },

        methods: {
            updateNavigation() {
                this.$emit('updateNavigation', {
                    hasBack: false,
                    canGoBack: false,
                    hasNext: true,
                    canGoNext: true,
                    nextSpecial: 'Confirm',
                });
            },

            formatPriceValue(price) {
                if (price == null) {
                    return '';
                }

                if (price === 0) {
                    return this.$t('ITEM_FREE');
                }

                return formatPrice(this.$store.state, price, 'standard');
            },

            getFormattedPrice(item) {
                if (item.price === 0 && (item.type === 'specialbox' || item.type === 'sticker')) {
                    return this.$t('ITEM_FREE');
                }

                // Don't show $0.00 price for accessories
                if (item.price === 0 && (item.type === 'extra')) {
                    return this.$t('ITEM_FREE');
                }

                if (item.price == null) {
                    return '';
                }

                return formatPrice(this.$store.state, item.price, 'standard');
            },

            editItem(item) {
                if (item.type === 'main') {
                    const selectedPop = this.isTwoPack ? this.$store.getters.popControllers.find((x) => x.id === item.id) : this.viewController;
                    const startGroup = selectedPop.blueprint.groups.find((g) => g.custom && g.custom['fun-start-group'] === 'true');

                    this.$store.dispatch('selectPop', selectedPop);
                    this.$store.dispatch('showGroup', startGroup);
                    this.$store.dispatch('log', {
                        level: 'info',
                        message: 'Edit POP!',
                    });
                    this.$store.dispatch('setIsNavMenuOpen', true);
                }
            },

            removeItem() {
                const pack = this.viewController.findPlacement('POPBuddyPack');

                if (pack) {
                    const toSelect = pack.components.find((x) => x.placeholder);

                    if (toSelect) {
                        this.viewController.updateComponent(pack.code, toSelect.code);
                    }
                }

                this.$store.dispatch('setPetPackInCart');
            },

            removeSticker() {
                const sticker = this.viewController.findPlacement(constants.PLACEMENT_STICKERS);

                if (sticker) {
                    const placeholder = sticker.components.find((c) => c.placeholder);

                    this.$store.dispatch('bumpVersion');

                    this.viewController.updateComponent(constants.PLACEMENT_STICKERS, placeholder.code);
                }
            },

            removeSeasonalBox() {
                this.$store.dispatch('removeSeasonalBox');
            },

            editContact() {
                this.$store.dispatch('showGroup', {
                    code: 'Contact',
                });
            },

            checkImage(i) {
                if (Array.isArray(i)) {
                    return i.map((x) => {
                        if (x.startsWith('//')) {
                            return `https:${i}`;
                        }

                        return x;
                    });
                }

                if (i && i.startsWith('//')) {
                    return `https:${i}`;
                }

                return i;
            },

            updateRenderer() {
                this.emitter.$emit('setRendererPlaceholder', {
                    owner: 'checkout',
                    placeholder: this.$refs.rendererPlaceholder,
                    immediate: true,
                });
            },

            getItemName(item) {
                let itemName = item.name;

                if (item.type === 'main') {
                    // eslint-disable-next-line
                    itemName = item.isFirstPop ? this.boxNames[0] : this.boxNames[1];
                }

                return itemName;
            },

            saveRecipe() {
                this.$store.dispatch('customizerDone', {
                    type: 'cart',
                    noBusy: true,
                    noVariants: true,
                    saveTemporary: true,
                }).then((recipeData) => {
                    this.$store.dispatch('setSavedRecipe', recipeData);

                    this.recordAbandonedCart(recipeData);
                });
            },

            recordAbandonedCart(recipeData) {
                if (!this.isKiosk) {
                    if (this.viewController.blueprint.custom['fun-save-abandoned'] === 'true') {
                        // Find preview.
                        const preview = recipeData.recipe.views.find((x) => x.code === 'Preview');

                        if (preview) {
                            const png = `https:${preview.previewPng}`;

                            const formData = new URLSearchParams();

                            let name = '';

                            if (this.$store.getters.isTwoPack) {
                                name = Object.values(this.$store.state.customizer.boxNamesByController).join(' & ');
                            } else {
                                name = this.$store.state.customizer.boxName;
                            }

                            formData.append('name', name);
                            formData.append('imageURL', png);
                            formData.append('recipeId', recipeData.recipe.id);

                            // Send POST request with form data
                            let targetUrl = '/on/demandware.store/Sites-FunkoUS-Site/en_US/Customizer-FireAbandonmentEvent';

                            if (this.$store.state.options.abandonedCartEndpoint) {
                                targetUrl = this.$store.state.options.abandonedCartEndpoint;
                            }

                            if (this.$store.state.options.abandonmentEventEndpoint) {
                                targetUrl = this.$store.state.options.abandonmentEventEndpoint;
                            }

                            axios.post(targetUrl, formData, {
                                headers: {
                                    'Content-Type': 'application/x-www-form-urlencoded',
                                },
                            });
                        }
                    }
                }
            },
        },
    };
</script>
